// Ensure imports are correct
import { connectWebSocket } from './socketU.js';
import { apiBaseUrl } from './config.js';
import { getAuthToken } from './auth.js';
import { t, confirmT, updateElementText } from './translationHelper.js';
import { showNotification } from './notif.js';


let knownItemIdsAtPageLoad = [];
let userHasInteracted = false; // Flag to track if the user has interacted with the popup

// Burst effect state for commodity exchange visuals
let burstParticles = [];
let burstCanvas = null;
let burstAnimationFrame = null;

// Commodity Exchange Burst Effect - creates animated particles
function createBurstEffect(container) {
  // Create canvas for burst particles
  burstCanvas = document.createElement('canvas');
  burstCanvas.style.cssText = `
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	z-index: 2;
  `;
  container.appendChild(burstCanvas);

  const ctx = burstCanvas.getContext('2d');
  burstCanvas.width = container.offsetWidth;
  burstCanvas.height = container.offsetHeight;

  // Create initial burst particles
  const particleCount = 30;
  for (let i = 0; i < particleCount; i++) {
	burstParticles.push({
	  x: Math.random() * burstCanvas.width,
	  y: Math.random() * burstCanvas.height,
	  vx: (Math.random() - 0.5) * 2,
	  vy: (Math.random() - 0.5) * 2,
	  life: Math.random() * 100 + 100,
	  maxLife: 200,
	  size: Math.random() * 3 + 2,
	  hue: 120 + Math.random() * 40 // Green range
	});
  }

  // Animation loop
  function animate() {
	ctx.clearRect(0, 0, burstCanvas.width, burstCanvas.height);

	burstParticles.forEach((particle, index) => {
	  // Update position
	  particle.x += particle.vx;
	  particle.y += particle.vy;
	  particle.life--;

	  // Bounce off edges
	  if (particle.x < 0 || particle.x > burstCanvas.width) particle.vx *= -1;
	  if (particle.y < 0 || particle.y > burstCanvas.height) particle.vy *= -1;

	  // Draw particle with glow
	  const alpha = particle.life / particle.maxLife;
	  ctx.save();
	  ctx.shadowBlur = 15;
	  ctx.shadowColor = `hsla(${particle.hue}, 100%, 50%, ${alpha})`;
	  ctx.fillStyle = `hsla(${particle.hue}, 100%, 50%, ${alpha * 0.8})`;
	  ctx.beginPath();
	  ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
	  ctx.fill();
	  ctx.restore();

	  // Remove dead particles and spawn new ones
	  if (particle.life <= 0) {
		burstParticles[index] = {
		  x: Math.random() * burstCanvas.width,
		  y: Math.random() * burstCanvas.height,
		  vx: (Math.random() - 0.5) * 2,
		  vy: (Math.random() - 0.5) * 2,
		  life: Math.random() * 100 + 100,
		  maxLife: 200,
		  size: Math.random() * 3 + 2,
		  hue: 120 + Math.random() * 40
		};
	  }
	});

	burstAnimationFrame = requestAnimationFrame(animate);
  }

  animate();
}

// Clean up burst effect
function cleanupBurstEffect() {
  if (burstAnimationFrame) {
	cancelAnimationFrame(burstAnimationFrame);
	burstAnimationFrame = null;
  }
  if (burstCanvas) {
	burstCanvas.remove();
	burstCanvas = null;
  }
  burstParticles = [];
}

export async function showRedeemOfferPopup(data) {
  console.log('Redeem offer received:', data);

  // Ensure socket is up (no-op if already connected)
  try { connectWebSocket?.(); } catch (_) {}

  // Remove any existing popup
  const existingPopup = document.getElementById("redeemOfferPopup");
  if (existingPopup) existingPopup.remove();

  // Build popup shell
  const RedeemPopup = document.createElement("div");
  RedeemPopup.id = "redeemOfferPopup";
  RedeemPopup.style.cssText = `
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 10001;
	font-family: 'bithigh', Arial, sans-serif;
	color: #00ff00;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background: linear-gradient(135deg, rgba(0, 40, 80, 0.95) 0%, rgba(0, 0, 0, 0.98) 100%);
  `;

  // Background video
  const video = document.createElement("video");
  video.id = "redeemPopupBackground";
  video.style.cssText = `
	position: absolute; top: 0; left: 0; width: 100%; height: 100%;
	object-fit: cover; z-index: -1; pointer-events: none; user-select: none;
  `;
  video.autoplay = true; video.loop = true; video.muted = true; video.playsInline = true;
  RedeemPopup.appendChild(video);

  // Foreground content with enhanced styling
  const content = document.createElement("div");
  content.id = "redeemContentBox";
  content.style.cssText = `
	position: relative;
	z-index: 3;
	padding: 40px 30px;
	background: linear-gradient(135deg, rgba(0, 40, 80, 0.85) 0%, rgba(0, 0, 0, 0.92) 100%);
	border: 3px solid #00ff00;
	border-radius: 8px;
	max-width: 85%;
	width: 500px;
	box-shadow:
	  0 0 30px rgba(0, 255, 0, 0.6),
	  inset 0 0 20px rgba(0, 255, 0, 0.1);
	animation: pulseGlow 2s ease-in-out infinite;
  `;

  // Get offer details
  const senderName = sanitize(data?.senderName || 'Unknown');
  const itemName = sanitize(data?.itemName || 'Unknown Item');

  content.innerHTML = `
	<div style="margin-bottom: 20px; font-size: 0.85rem; color: #00ff00; opacity: 0.7; letter-spacing: 2px;">
	  INCOMING COMMODITY EXCHANGE
	</div>
	<h2 style="color: #00ff00; font-size: 1.8rem; margin: 20px 0; text-shadow: 0 0 10px rgba(0, 255, 0, 0.8);">
	  ${t('Redemption Offer', 'Redemption Offer')}
	</h2>
	<div style="background: rgba(0, 255, 0, 0.1); border: 1px solid #00ff00; border-radius: 5px; padding: 15px; margin: 20px 0;">
	  <p style="color: #00ff00; font-size: 1rem; margin: 8px 0;">
		<strong>${t('From:', 'From:')}</strong> ${senderName}
	  </p>
	  <p style="color: #00ff00; font-size: 1rem; margin: 8px 0;">
		<strong>${t('Commodity:', 'Commodity:')}</strong> ${itemName}
	  </p>
	</div>
	<div style="display: flex; gap: 15px; justify-content: center; margin-top: 30px;">
	  <button id="acceptRedeemButton" style="
		flex: 1;
		max-width: 180px;
		padding: 15px 25px;
		background: linear-gradient(135deg, rgba(0, 100, 0, 0.9) 0%, rgba(0, 50, 0, 0.9) 100%);
		border: 2px solid #00ff00;
		border-radius: 5px;
		color: #00ff00;
		font-family: 'bithigh', sans-serif;
		font-size: 1.1rem;
		cursor: pointer;
		box-shadow: 0 0 15px rgba(0, 255, 0, 0.4);
		transition: all 0.3s ease;
		text-shadow: 0 0 5px rgba(0, 255, 0, 0.6);
	  " onmouseover="this.style.boxShadow='0 0 25px rgba(0, 255, 0, 0.8)'; this.style.transform='scale(1.05)'" onmouseout="this.style.boxShadow='0 0 15px rgba(0, 255, 0, 0.4)'; this.style.transform='scale(1)'">
		✓ ${t('Accept', 'Accept')}
	  </button>
	  <button id="declineRedeemButton" style="
		flex: 1;
		max-width: 180px;
		padding: 15px 25px;
		background: linear-gradient(135deg, rgba(100, 0, 0, 0.9) 0%, rgba(50, 0, 0, 0.9) 100%);
		border: 2px solid #ff6666;
		border-radius: 5px;
		color: #ff6666;
		font-family: 'bithigh', sans-serif;
		font-size: 1.1rem;
		cursor: pointer;
		box-shadow: 0 0 15px rgba(255, 0, 0, 0.4);
		transition: all 0.3s ease;
		text-shadow: 0 0 5px rgba(255, 0, 0, 0.6);
	  " onmouseover="this.style.boxShadow='0 0 25px rgba(255, 0, 0, 0.8)'; this.style.transform='scale(1.05)'" onmouseout="this.style.boxShadow='0 0 15px rgba(255, 0, 0, 0.4)'; this.style.transform='scale(1)'">
		✕ ${t('Decline', 'Decline')}
	  </button>
	</div>
	<div id="timer" style="font-size: 1.5rem; text-align: center; color: #00ff00; margin-top: 25px; font-weight: bold; text-shadow: 0 0 15px rgba(0, 255, 0, 0.9);"></div>
  `;

  // Add pulsing glow animation
  const style = document.createElement('style');
  style.textContent = `
	@keyframes pulseGlow {
	  0%, 100% {
		box-shadow:
		  0 0 30px rgba(0, 255, 0, 0.6),
		  inset 0 0 20px rgba(0, 255, 0, 0.1);
		border-color: #00ff00;
	  }
	  50% {
		box-shadow:
		  0 0 50px rgba(0, 255, 0, 0.9),
		  0 0 100px rgba(0, 255, 0, 0.4),
		  inset 0 0 30px rgba(0, 255, 0, 0.2);
		border-color: #00ff66;
	  }
	}
  `;
  document.head.appendChild(style);
  
  const escHandler = (e) => { if (e.key === 'Escape') { cleanup(); document.removeEventListener('keydown', escHandler); } };
  document.addEventListener('keydown', escHandler);
  
  RedeemPopup.addEventListener('click', (e) => {
	// only close if the click is on the outer container (not content)
	if (e.target === RedeemPopup) cleanup();
  });
  
  RedeemPopup.appendChild(content);
  document.body.appendChild(RedeemPopup);
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  // Start burst particle effect
  createBurstEffect(content);

function cleanup() {
	clearInterval(timerInterval);
	cleanupBurstEffect();
	if (RedeemPopup && RedeemPopup.parentNode) {
	  RedeemPopup.parentNode.removeChild(RedeemPopup);
	}
	document.body.style.overflow = prevOverflow;
	document.removeEventListener('keydown', escHandler);
  }

  // Load random background video
  try {
	const videoBaseUrl = `${apiBaseUrl}/uploads/specialBacks/redeemBack/`;
	const totalVideos = 45;
	const videoIndex = Math.floor(Math.random() * totalVideos) + 1;
	const videoUrl = `${videoBaseUrl}${videoIndex}/playlist.m3u8`;

	if (window.Hls && window.Hls.isSupported()) {
	  const hls = new window.Hls();
	  hls.loadSource(videoUrl);
	  hls.attachMedia(video);
	  hls.on(window.Hls.Events.MANIFEST_PARSED, () => video.play().catch(()=>{}));
	} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
	  video.src = videoUrl;
	  video.addEventListener('loadedmetadata', () => video.play().catch(()=>{}));
	}
  } catch (e) {
	// Non-fatal
	console.warn('Redeem background video load error:', e);
  }

  // Timer with enhanced formatting
  const timerElement = document.getElementById('timer');
  let remainingTime = Number(data?.timeoutSeconds) || 10;
  const tick = () => {
	if (timerElement) {
	  const secondsLabel = remainingTime === 1 ? t('second', 'second') : t('seconds', 'seconds');
	  timerElement.textContent = `${remainingTime} ${secondsLabel}`;
	}
  };
  tick();

  const timerInterval = setInterval(() => {
	remainingTime -= 1;
	tick();
	if (remainingTime <= 0) {
	  clearInterval(timerInterval);
	  if (timerElement) {
		timerElement.textContent = t('Time expired', 'Time expired');
		timerElement.style.color = '#ff6666';
		timerElement.style.textShadow = '0 0 15px rgba(255, 0, 0, 0.9)';
	  }
	  setTimeout(() => cleanup(), 1000); // Brief delay before closing
	}
  }, 1000);

  // Helpers
  const s = (typeof window !== 'undefined') ? window.popupTrackerSocket : null;
  const myPortfolioId = localStorage.getItem('portfolioId') || '';
  const initiatorUserId = sanitize(data?.initiatorUserId);
  const preselectedItemId   = sanitize(data?.preselectedItems?.[0]?.itemId   ?? data?.preselectedItemId);
  const preselectedItemName = sanitize(data?.preselectedItems?.[0]?.itemName ?? data?.preselectedItemName);
  const offeredItemId       = sanitize(data?.itemId);
  const transactionItemId   = sanitize(data?.itemId);

  async function postDecision(url, pointsAdjustment) {
	return fetch(`${apiBaseUrl}${url}`, {
	  method: 'POST',
	  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
	  body: JSON.stringify({
		initiatorUserId,
		targetUserId: myPortfolioId,
		pointsAdjustment,
		preselectedItemId,
		preselectedItemName,
		offeredItemId,
		transactionItemId
	  })
	});
  }

  function emitDecision(accepted) {
	if (!s || !s.connected) {
	  showNotification(t('redeem.ws_not_connected', 'Not connected. Please try again.'), false);
	  return;
	}
	s.emit('redeemResponse', {
	  accepted,
	  preselectedItemId,
	  preselectedItemName,
	  initiatorUserId,
	  targetUserId: myPortfolioId
	}, (ack) => {
	  if (ack && ack.success === false) {
		console.error('redeemResponse ack error:', ack.error);
		showNotification(t('redeem.notify_failed', 'Failed to notify initiator.'), false);
	  }
	});
  }

  // Wire buttons
  const acceptButton  = document.getElementById("acceptRedeemButton");
  const declineButton = document.getElementById("declineRedeemButton");

  if (acceptButton) {
	acceptButton.addEventListener('click', async () => {
	  clearInterval(timerInterval);
	  try {
		const resp = await postDecision('/api/acceptRedemption', 70);
		if (!resp.ok) {
		  console.error('Failed to accept redeem.', await resp.text());
		  showNotification(t('redeem.accept_failed', 'Failed to accept redemption.'), false);
		} else {
		  console.log('Redeem accepted successfully.');
		  emitDecision(true);
		}
	  } catch (err) {
		console.error('Error accepting redeem:', err);
		showNotification(t('redeem.accept_failed', 'Failed to accept redemption.'), false);
	  }
	  cleanup();
	});
  }

  if (declineButton) {
	declineButton.addEventListener('click', async () => {
	  clearInterval(timerInterval);
	  try {
		const resp = await postDecision('/api/declineRedemption', -70);
		if (!resp.ok) {
		  console.error('Failed to decline redeem.', await resp.text());
		  showNotification(t('redeem.decline_failed', 'Failed to decline redemption.'), false);
		} else {
		  console.log('Redeem declined successfully.');
		  emitDecision(false);
		}
	  } catch (err) {
		console.error('Error declining redeem:', err);
		showNotification(t('redeem.decline_failed', 'Failed to decline redemption.'), false);
	  }
	  cleanup();
	});
  }

  // Block interactions with the background video
  ['click','dblclick','contextmenu'].forEach(evt =>
	video.addEventListener(evt, e => { e.stopPropagation(); e.preventDefault(); })
  );
}

function sanitize(input) {
  const div = document.createElement('div');
  div.textContent = (input ?? '').toString();
  return div.innerHTML;
}
